import { useEffect, useState } from "react";

const scriptUrl = import.meta.env.VITE_AML_SDK_URL || "";
const clientIdentifier = import.meta.env.VITE_AML_CLIENT_IDENTIFIER || "";
const amlNs = import.meta.env.VITE_AML_NS || "";

declare global {
	interface Window {
		amlSdk?: {
			init: (token: string) => void;
			initialize: (selector: string) => void;
			destroy: () => void;
			displayQuestionnaire: (
				client: { ns: string; id: string },
				procedure: { controlProcedureId: string },
			) => Promise<void>;
		};
	}
}

type UseAmlSdkOptions = {
	onError?: (error: Error) => void;
};

export const useAmlSdk = ({ onError }: UseAmlSdkOptions = {}) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const isConfigured = Boolean(scriptUrl && clientIdentifier && amlNs);

	useEffect(() => {
		if (!isConfigured) {
			onError?.(
				new Error(
					"AML SDK is not configured. Please set VITE_AML_SDK_URL and VITE_AML_CLIENT_IDENTIFIER environment variables.",
				),
			);
			return;
		}

		if (window.amlSdk) {
			setIsLoaded(true);
			return;
		}

		const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
		if (existingScript) {
			setIsLoading(true);
			existingScript.addEventListener("load", () => {
				setIsLoading(false);
				setIsLoaded(true);
				window.amlSdk?.init(clientIdentifier);
			});
			return;
		}

		setIsLoading(true);
		const script = document.createElement("script");
		script.src = scriptUrl;
		script.type = "module";
		script.async = true;

		script.onload = () => {
			setIsLoading(false);
			if (window.amlSdk) {
				window.amlSdk.init(clientIdentifier);
				setIsLoaded(true);
			} else {
				onError?.(new Error("AML SDK failed to initialize"));
			}
		};

		script.onerror = () => {
			setIsLoading(false);
			onError?.(new Error("Failed to load AML SDK script"));
		};

		document.body.appendChild(script);
	}, [isConfigured, onError]);

	const displayQuestionnaire = (controlProcedureId: string) => {
		if (!window.amlSdk) {
			onError?.(new Error("AML SDK not initialized"));
			return;
		}

		window.amlSdk.displayQuestionnaire(
			{ ns: amlNs, id: clientIdentifier },
			{ controlProcedureId },
		);
	};

	return {
		isConfigured,
		isLoaded,
		isLoading,
		displayQuestionnaire,
	};
};
